import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Search, FileSpreadsheet, ArrowUpDown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaterialVariancePage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('variance_pct');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/reports/material-variance`, { headers: getAuthHeader() });
      setData(r.data.data || []);
    } catch { setData([]); }
    setLoading(false);
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(false); }
  };

  const filtered = data
    .filter(r => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (r.work_order_number || '').toLowerCase().includes(s) ||
             (r.so_number || '').toLowerCase().includes(s) ||
             (r.material_name || '').toLowerCase().includes(s) ||
             (r.wo_item_name || '').toLowerCase().includes(s) ||
             (r.issued_by_name || '').toLowerCase().includes(s);
    })
    .sort((a, b) => {
      const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
      return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const getVarianceColor = (pct) => {
    if (pct >= 10) return 'text-red-700 bg-red-50 font-semibold';
    if (pct >= 5) return 'text-amber-700 bg-amber-50';
    return 'text-yellow-700 bg-yellow-50';
  };

  const handleExport = () => {
    const headers = ['WO Number', 'SO Number', 'WO Item', 'Material', 'Type', 'Required', 'Issued', 'Excess', 'Excess %', 'Process', 'Job Card', 'JC Qty', 'Issued By'];
    const rows = filtered.map(r => [
      r.work_order_number, r.so_number || '', r.wo_item_name, r.material_name,
      r.material_type === 'extra' ? 'Extra (Not in BOM)' : 'BOM',
      r.required_qty, r.issued_qty, r.variance, r.variance_pct + '%',
      r.process_name, r.job_letter, r.jc_qty_issued, r.issued_by_name
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `material-variance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const excessCount = data.filter(r => r.variance > 0).length;
  const extraCount = data.filter(r => r.material_type === 'extra').length;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="material-variance-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Material Variance Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Per job card breakdown of excess material usage and non-BOM additions.</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-muted-foreground">Total Entries</p>
              <p className="text-2xl font-bold">{filtered.length}</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-red-600">Excess Issuances</p>
              <p className="text-2xl font-bold text-red-700">{excessCount}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-amber-600">Non-BOM Additions</p>
              <p className="text-2xl font-bold text-amber-700">{extraCount}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[250px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search WO, material, item, or supervisor..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="variance-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv">
            <FileSpreadsheet className="w-4 h-4 mr-1" />Export CSV
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Variance Details ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="cursor-pointer" onClick={() => toggleSort('work_order_number')}>
                      <span className="flex items-center gap-1">WO <ArrowUpDown className="w-3 h-3" /></span>
                    </TableHead>
                    <TableHead>WO Item</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-center">Type</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => toggleSort('required_qty')}>
                      <span className="flex items-center justify-end gap-1">Required <ArrowUpDown className="w-3 h-3" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => toggleSort('issued_qty')}>
                      <span className="flex items-center justify-end gap-1">Issued <ArrowUpDown className="w-3 h-3" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => toggleSort('variance')}>
                      <span className="flex items-center justify-end gap-1">Excess <ArrowUpDown className="w-3 h-3" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer w-[80px]" onClick={() => toggleSort('variance_pct')}>
                      <span className="flex items-center justify-end gap-1">% <ArrowUpDown className="w-3 h-3" /></span>
                    </TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead className="text-center">JC</TableHead>
                    <TableHead>Issued By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No material variance found</TableCell></TableRow>
                  ) : (
                    filtered.map((r, idx) => {
                      const colorClass = getVarianceColor(r.variance_pct);
                      return (
                        <TableRow key={idx} className="hover:bg-muted/30">
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">{r.work_order_number}</Badge>
                            {r.so_number && !r.work_order_number?.startsWith(r.so_number) && (
                              <span className="text-xs text-muted-foreground ml-1">({r.so_number})</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm max-w-[130px] truncate" title={r.wo_item_name}>{r.wo_item_name}</TableCell>
                          <TableCell className="font-medium text-sm">{r.material_name}</TableCell>
                          <TableCell className="text-center">
                            {r.material_type === 'extra' ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Not in BOM</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">BOM</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {r.required_qty > 0 ? r.required_qty : <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">{r.issued_qty}</TableCell>
                          <TableCell className="text-right">
                            <span className={`px-2 py-0.5 rounded text-sm ${colorClass}`}>+{r.variance}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`px-2 py-0.5 rounded text-sm ${colorClass}`}>+{r.variance_pct}%</span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.process_name}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className="text-xs">{r.job_letter}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{r.issued_by_name || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
